import { useCallback, useEffect, useState } from 'react';
import { collection, doc as docRef, query, where, limit, orderBy, onSnapshot, DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { uploadToPath } from '../../services/storageService';
import {
  listConversations, createConversation, deleteConversation, sendConversationMessage,
  removeConversationMember, removeConversationParticipant,
  type ConversationSummary, type MessageAttachment, type ProactiveLevel,
} from '../../services/conversationService';
import { isTextFile } from '../../utils/readTextFile';
import { isHtmlFile, htmlToText } from '../../utils/htmlText';
import { trackConversationStarted, trackAgentMessageSent } from '../../services/analyticsService';
import type { AcceptedAttachment } from '../../utils/chatAttachments';
import type { ChatSession, ChatMsg, MemberSpec } from './types';

const EXCERPT_CHARS = 20_000; // cap text we send to the model (cost guard)

/** Read a file's leading text (≤~2 MB scanned), truncated — no reject on size. */
function readTextTruncated(file: File): Promise<string> {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').slice(0, EXCERPT_CHARS));
    r.onerror = () => resolve('');
    r.readAsText(file.slice(0, 2_000_000));
  });
}

/** Upload a binary attachment to owner-scoped Storage; returns its path. */
async function uploadAttachment(file: File, uid: string, conversationId: string): Promise<string> {
  const safe = file.name.replace(/[^\w.-]+/g, '_').slice(-100);
  const path = `chat-attachments/${uid}/${conversationId}/${Date.now()}_${safe}`;
  await uploadToPath(path, file, file.type || 'application/octet-stream');
  return path;
}

/**
 * Turn validated attachments into the wire shape: text/CSV/MD are read to text
 * client-side; binaries (PDF/DOCX/images) upload to Storage so the server can extract
 * (PDF/DOCX) or use them later (images). Best-effort — a failed upload still references
 * the file by name.
 */
async function toWireAttachments(items: AcceptedAttachment[], conversationId: string, uid?: string): Promise<MessageAttachment[]> {
  const out: MessageAttachment[] = [];
  for (const { file, kind } of items) {
    const html = isHtmlFile(file.name, file.type);
    const extractable = html || kind === 'data' || (kind === 'document' && isTextFile(file.name, file.type));
    let text_excerpt: string | null = null;
    let storage_path: string | null = null;
    if (extractable) {
      const raw = await readTextTruncated(file);
      text_excerpt = (html ? htmlToText(raw).slice(0, EXCERPT_CHARS) : raw) || null;
      // Fallback: the client read produced nothing (FileReader error / odd encoding / non-UTF8 CSV) —
      // upload so the SERVER can extract it, instead of the file silently becoming "no text extracted".
      if (!text_excerpt && uid) {
        try { storage_path = await uploadAttachment(file, uid, conversationId); }
        catch { storage_path = null; }
      }
    } else if (uid) {
      try { storage_path = await uploadAttachment(file, uid, conversationId); }
      catch { storage_path = null; }
    }
    out.push({ name: file.name, mime: file.type || '', kind, size: file.size, text_excerpt, storage_path });
  }
  return out;
}

const uid = () => `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
const ts = (iso: string) => Date.parse(iso) || Date.now();

// Per-user, per-conversation "last read" markers (localStorage) — powers the unread dot in the
// sidebar. No server-side read receipts; a lightweight local baseline is enough for a "new message"
// indicator. Keyed by uid so multiple accounts on one browser don't cross-signal.
const readsKey = () => `wisetree_chat_reads_v1:${auth.currentUser?.uid || 'anon'}`;
const loadReads = (): Record<string, number> => {
  try { return JSON.parse(localStorage.getItem(readsKey()) || '{}') || {}; } catch { return {}; }
};
const persistReads = (r: Record<string, number>) => {
  try { localStorage.setItem(readsKey(), JSON.stringify(r)); } catch { /* private mode / quota — dot is best-effort */ }
};

/** Map a backend conversation summary into the client ChatSession shape. */
const toSession = (c: ConversationSummary): ChatSession => ({
  id: c.id,
  title: c.title,
  slug: c.slug,
  ownerId: c.user_id,
  members: (c.members || []) as MemberSpec[],
  humanMembers: c.human_members || [],
  storageLocation: c.storage_location || 'cloud',
  logoUrl: c.logo_url ?? null,
  language: c.language ?? null,
  agents_native_language: !!c.agents_native_language,
  messages: [],
  createdAt: ts(c.created_at),
  updatedAt: ts(c.updated_at),
});

/**
 * A generative-UI `spec` is stored in Firestore as a JSON STRING (charts/tables hold nested
 * arrays Firestore rejects — see conversationRepository). The realtime listener reads the raw
 * doc, so parse the string back to an object here; tolerate a legacy object or bad JSON.
 */
const parseSpec = (v: unknown): unknown => {
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return undefined; } }
  return v || undefined;
};

const toMsgFromDoc = (d: QueryDocumentSnapshot<DocumentData>): ChatMsg => {
  const x = d.data();
  return {
    id: d.id,
    clientMsgId: x.client_msg_id || undefined,
    author: x.author,
    authorUid: x.author_uid || undefined,
    authorName: x.author_name,
    avatarIcon: x.avatar_icon || undefined,
    content: x.content,
    spec: parseSpec(x.spec),
    attachments: x.attachments || undefined,
    reactions: x.reactions || undefined,
    bookmarked: x.bookmarked || undefined,
    language: x.language || undefined,
    translations: x.translations || undefined,
    spec_translations: x.spec_translations || undefined,
    citations: Array.isArray(x.citations) && x.citations.length ? x.citations : undefined,
    voicePath: x.voice_path || undefined,
    voiceMime: x.voice_mime || undefined,
    ts: x.created_at?.toMillis ? x.created_at.toMillis() : Date.now(),
  };
};

/**
 * Realtime chat sessions for the chat-first dashboard. Conversations persist in
 * Firestore; the active conversation's messages stream in live via onSnapshot, so
 * each specialist's reply appears the moment the backend writes it. The user's own
 * message is echoed optimistically (temp id) and reconciled when the server copy lands.
 */
export function useChatSessions() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  // Count in-flight replies (the user can fire several messages while agents work) so
  // the typing indicator stays until ALL are done, not just the first to finish.
  const [pendingReplies, setPendingReplies] = useState(0);
  const sending = pendingReplies > 0;
  const [respondingIds, setRespondingIds] = useState<string[]>([]);
  // Live last-activity per conversation (updated_at millis) + the user's local "last read" markers.
  // A chat is UNREAD when its latest activity is newer than what the user last saw — drives the dot.
  const [activity, setActivity] = useState<Record<string, number>>({});
  const [reads, setReads] = useState<Record<string, number>>(loadReads);

  useEffect(() => {
    listConversations()
      .then((convs) => setSessions(convs.map(toSession)))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  // Realtime last-activity across ALL the user's chats (owner IS in participant_uids), so a new
  // message in a background group lights its dot without opening it. Only tracks updated_at — cheap,
  // no message payloads. (Same index the backend list query uses.)
  useEffect(() => {
    const u = auth.currentUser?.uid;
    if (!u) return;
    const q = query(
      collection(db, 'conversations'),
      where('participant_uids', 'array-contains', u),
      orderBy('updated_at', 'desc'),
      limit(100),
    );
    const unsub = onSnapshot(q, (snap) => {
      const map: Record<string, number> = {};
      snap.docs.forEach((d) => {
        const t = d.get('updated_at') as { toMillis?: () => number } | string | undefined;
        map[d.id] = (t && typeof t !== 'string' && t.toMillis) ? t.toMillis() : (Date.parse(String(t)) || 0);
      });
      setActivity(map);
    }, () => { /* permission/transient — simply no unread signal */ });
    return () => unsub();
  }, []);

  // Baseline: the first time we ever observe a chat, treat its current state as "seen" so a returning
  // user isn't greeted by a wall of dots. Genuine unread = activity that advances PAST this baseline.
  useEffect(() => {
    setReads((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [id, updated] of Object.entries(activity)) {
        if (next[id] === undefined) { next[id] = updated; changed = true; }
      }
      if (changed) persistReads(next);
      return changed ? next : prev;
    });
  }, [activity]);

  /** Mark a conversation read up to now (or a given activity time) — clears its unread dot. */
  const markRead = useCallback((id: string, at: number = Date.now()) => {
    setReads((prev) => {
      if ((prev[id] || 0) >= at) return prev;
      const next = { ...prev, [id]: at };
      persistReads(next);
      return next;
    });
  }, []);

  const patch = useCallback((id: string, fn: (s: ChatSession) => ChatSession) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? fn(s) : s)));
  }, []);

  /** Set a single message's emoji reactions in local state (optimistic + server-confirm). */
  const applyReactions = useCallback((conversationId: string, messageId: string, reactions: ChatMsg['reactions']) => {
    patch(conversationId, (s) => ({ ...s, messages: s.messages.map((m) => (m.id === messageId ? { ...m, reactions } : m)) }));
  }, [patch]);

  /** Patch a single message's fields locally (e.g. { bookmarked }) — optimistic. */
  const patchMessage = useCallback((conversationId: string, messageId: string, fields: Partial<ChatMsg>) => {
    patch(conversationId, (s) => ({ ...s, messages: s.messages.map((m) => (m.id === messageId ? { ...m, ...fields } : m)) }));
  }, [patch]);

  /** Remove a message locally — optimistic delete. */
  const removeMessage = useCallback((conversationId: string, messageId: string) => {
    patch(conversationId, (s) => ({ ...s, messages: s.messages.filter((m) => m.id !== messageId) }));
  }, [patch]);

  // Subscribe to a conversation's messages AND its doc (members / joined humans /
  // storage / logo update live — e.g. a guest accepting an invite). Returns unsubscribe.
  const watch = useCallback((conversationId: string | null) => {
    if (!conversationId) return () => {};
    const q = query(collection(db, 'conversations', conversationId, 'messages'), orderBy('created_at', 'asc'));
    const unsubMsgs = onSnapshot(q, (snap) => {
      const server = snap.docs.map(toMsgFromDoc);
      patch(conversationId, (s) => {
        // Guard against a transient EMPTY snapshot served from CACHE (fires on reconnect / tab
        // refocus / re-sync) blanking a stream that already has server-backed messages — keep the
        // last known data and wait for the real server snapshot. A genuinely empty chat is
        // confirmed by the server (fromCache === false), so real clears still go through.
        if (!server.length && snap.metadata.fromCache && s.messages.some((m) => !m.id.startsWith('temp-'))) {
          return s;
        }
        // Drop an optimistic echo once its server copy arrives — matched by the client id the
        // server echoes back (robust to identical text). Temps without a server match (e.g. local
        // system notes, or an echo still in flight) are kept.
        const serverClientIds = new Set(server.map((m) => m.clientMsgId).filter(Boolean));
        const local = s.messages.filter((m) => m.id.startsWith('temp-') && !(m.clientMsgId && serverClientIds.has(m.clientMsgId)));
        return { ...s, messages: [...server, ...local] };
      });
    }, () => { /* transient/permission errors: keep last known messages */ });
    const unsubDoc = onSnapshot(docRef(db, 'conversations', conversationId), (d) => {
      const x = d.data();
      if (!x) return;
      patch(conversationId, (s) => ({
        ...s,
        title: x.title ?? s.title,
        members: (x.members || s.members) as MemberSpec[],
        humanMembers: x.human_members ?? s.humanMembers,
        storageLocation: x.storage_location ?? s.storageLocation,
        logoUrl: x.logo_url ?? s.logoUrl,
      }));
    }, () => { /* ignore */ });
    return () => { unsubMsgs(); unsubDoc(); };
  }, [patch]);

  const createSession = useCallback(async (members: MemberSpec[], name?: string, proactiveLevel?: ProactiveLevel): Promise<ChatSession | null> => {
    try {
      // No name → send empty so the backend auto-assigns a unique cloud-style name
      // (e.g. "azure-falcon-12") instead of the agents' (non-unique) names.
      const title = (name && name.trim()) || '';
      const c = await createConversation(members, title, proactiveLevel);
      const created = toSession(c);
      setSessions((prev) => [created, ...prev]);
      trackConversationStarted(c.id, members.length);
      // Return the whole session (incl. its slug) — callers navigate by slug immediately, before
      // React has re-rendered `sessions`, so they can't look the slug up from state yet.
      return created;
    } catch (e) {
      // Surface instead of swallowing — a silent null here reads as "Start Chat does nothing".
      console.error('[createSession] failed to create conversation:', e);
      return null;
    }
  }, []);

  /**
   * Delete a chat for good, OPTIMISTICALLY — the row goes the instant the creator confirms, and the previous
   * list is put back if the server refuses, so a delete that never happened can never look like it did. The
   * failure is RE-THROWN rather than swallowed: the caller owns the apology (a silent revert reads as a bug).
   */
  const deleteSession = useCallback(async (id: string) => {
    const before = sessions;
    setSessions((prev) => prev.filter((s) => s.id !== id));
    try { await deleteConversation(id); } catch (e) { setSessions(before); throw e; }
  }, [sessions]);

  /**
   * Apply a conversation summary to state — UPSERT, not just patch. A crew chat founded server-side by
   * `recruitCrewAgent` is a conversation this list has never seen (it was loaded once at mount), so a
   * pure patch would drop it and the right pane would stay empty until a reload.
   */
  const applyUpdate = useCallback((c: ConversationSummary) => {
    setSessions((prev) => (prev.some((s) => s.id === c.id)
      ? prev.map((s) => (s.id === c.id ? { ...toSession(c), messages: s.messages } : s))
      : [toSession(c), ...prev]));
  }, []);

  const removeMember = useCallback(async (conversationId: string, memberId: string) => {
    const c = await removeConversationMember(conversationId, memberId);
    applyUpdate(c);
  }, [applyUpdate]);

  const removeHuman = useCallback(async (conversationId: string, memberUid: string) => {
    const c = await removeConversationParticipant(conversationId, memberUid);
    applyUpdate(c);
  }, [applyUpdate]);

  const send = useCallback(async (session: ChatSession, text: string, targetIds?: string[], opts: { expectReply?: boolean; attachments?: AcceptedAttachment[]; mediaAspect?: string; mediaSize?: string; musicStyle?: string; musicKind?: string; mediaStyle?: string; durationSec?: number } = {}) => {
    const wireAttachments = opts.attachments?.length
      ? await toWireAttachments(opts.attachments, session.id, auth.currentUser?.uid)
      : undefined;
    const genOpts = (opts.mediaAspect || opts.mediaSize || opts.musicStyle || opts.musicKind || opts.mediaStyle || opts.durationSec)
      ? { mediaAspect: opts.mediaAspect, mediaSize: opts.mediaSize, musicStyle: opts.musicStyle, musicKind: opts.musicKind, mediaStyle: opts.mediaStyle, durationSec: opts.durationSec }
      : undefined;
    // Client id echoed back on the server copy so the optimistic bubble reconciles by ID, not by
    // content — sending the SAME text twice no longer drops the second echo.
    const clientMsgId = uid();
    const echo: ChatMsg = { id: `temp-${clientMsgId}`, clientMsgId, author: 'you', authorName: 'You', content: text, attachments: wireAttachments, ts: Date.now() };
    patch(session.id, (s) => ({ ...s, messages: [...s.messages, echo] }));
    trackAgentMessageSent(session.id, { attachments: wireAttachments?.length || 0, targeted: !!(targetIds && targetIds.length) });
    const systemNote = (content: string) => patch(session.id, (s) => ({
      ...s,
      messages: [...s.messages, { id: `temp-${uid()}`, author: 'system', authorName: 'Wisetree AI', content, ts: Date.now() }],
    }));

    // @human-only message: directed at the person — persist it, but don't expect (or
    // wait on) an agent reply, and don't show a "no reply" notice.
    if (opts.expectReply === false) {
      try { await sendConversationMessage(session.id, text, targetIds, wireAttachments, clientMsgId, genOpts); }
      catch (e: any) { systemNote(e?.message || 'Could not send your message. Please try again.'); }
      return;
    }

    // With an @mention we know who's replying; otherwise the backend picks one
    // relevant member, so we show a generic "replying" status (empty list).
    setRespondingIds(targetIds && targetIds.length ? targetIds : []);
    setPendingReplies((n) => n + 1);
    try {
      // Replies stream back via onSnapshot; the HTTP result tells us if NONE came
      // (e.g. the model was unreachable) so we can surface that instead of silence.
      const res = await sendConversationMessage(session.id, text, targetIds, wireAttachments, clientMsgId, genOpts);
      if (!res || !res.replies || res.replies.length === 0) {
        systemNote(res?.busy
          ? 'The AI is very busy right now — please try again in a moment.'
          : "No reply just now — the specialists couldn't respond. Please try again.");
      }
      // Rapport (Link) still grows server-side from this exchange (personalization / proactive check-ins);
      // it's intentionally not surfaced to the user, so no level-up toast here.
    } catch (e: any) {
      systemNote(e?.message || 'Could not reach the specialists. Please try again.');
    } finally {
      // Only clear the "replying" hint when the LAST in-flight reply finishes.
      setPendingReplies((n) => {
        const left = Math.max(0, n - 1);
        if (left === 0) setRespondingIds([]);
        return left;
      });
    }
  }, [patch]);

  return { sessions, loading, sending, respondingIds, activity, reads, markRead, watch, createSession, deleteSession, send, applyUpdate, applyReactions, patchMessage, removeMessage, removeMember, removeHuman };
}
